/**
 * User Stickies Types
 *
 * Type definitions for user-created sticky notes that appear on ALL /app pages.
 * Separate from the fixed Goals/Focus panels that only appear on Dashboard.
 */

/**
 * Available sticky note colors
 * Cycle through these colors when creating new stickies
 */
export type StickyColor = 'amber' | 'sky' | 'green' | 'purple' | 'pink';

/**
 * Position coordinates for sticky placement
 */
export interface StickyPosition {
  x: number;
  y: number;
}

/**
 * Size dimensions for sticky notes
 */
export interface StickySize {
  width: number;
  height: number;
}

/**
 * User-created sticky note data model
 */
export interface UserStickyNote {
  id: string;           // UUID
  title: string;        // Editable, default "New Note"
  content: string;
  isCollapsed: boolean;
  position: StickyPosition;
  size: StickySize;     // Width and height for resizable notes
  color: StickyColor;
  createdAt: number;    // Unix timestamp
  updatedAt: number;    // Unix timestamp
}

/**
 * Color scheme for sticky styling with dark mode support
 * Includes Dynamics theme overrides for professional grey appearance
 */
export interface StickyColorScheme {
  bg: string;
  border: string;
  header: string;
  headerText: string;
  iconColor: string;
  pillBg: string;
  pillBorder: string;
}

/**
 * Dynamics theme color scheme (professional grey, solid background)
 * Used when theme-dynamics class is active on document root
 * Uses standard Tailwind classes - components check isDynamicsTheme to apply
 * light-mode-only styling and skip dark: variants
 */
export const dynamicsColorScheme: StickyColorScheme = {
  bg: 'bg-white',  // Clean white background for Dynamics
  border: 'border-gray-300',  // Subtle grey border
  header: 'bg-gray-100',  // Light grey header
  headerText: 'text-gray-900',  // Dark text for readability
  iconColor: 'text-gray-600',  // Grey icons
  pillBg: 'bg-white',
  pillBorder: 'border-gray-300',
};

/**
 * All sticky color schemes mapped by color name
 * Supports light, dark, and default themes using Tailwind dark: variants
 */
export const stickyColorSchemes: Record<StickyColor, StickyColorScheme> = {
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-700',
    header: 'bg-amber-100 dark:bg-amber-900/50',
    headerText: 'text-amber-900 dark:text-amber-100',
    iconColor: 'text-amber-600 dark:text-amber-400',
    pillBg: 'bg-amber-50 dark:bg-amber-950/40',
    pillBorder: 'border-amber-200 dark:border-amber-700',
  },
  sky: {
    bg: 'bg-sky-50 dark:bg-sky-950/40',
    border: 'border-sky-200 dark:border-sky-700',
    header: 'bg-sky-100 dark:bg-sky-900/50',
    headerText: 'text-sky-900 dark:text-sky-100',
    iconColor: 'text-sky-600 dark:text-sky-400',
    pillBg: 'bg-sky-50 dark:bg-sky-950/40',
    pillBorder: 'border-sky-200 dark:border-sky-700',
  },
  green: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    border: 'border-emerald-200 dark:border-emerald-700',
    header: 'bg-emerald-100 dark:bg-emerald-900/50',
    headerText: 'text-emerald-900 dark:text-emerald-100',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    pillBg: 'bg-emerald-50 dark:bg-emerald-950/40',
    pillBorder: 'border-emerald-200 dark:border-emerald-700',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-950/40',
    border: 'border-purple-200 dark:border-purple-700',
    header: 'bg-purple-100 dark:bg-purple-900/50',
    headerText: 'text-purple-900 dark:text-purple-100',
    iconColor: 'text-purple-600 dark:text-purple-400',
    pillBg: 'bg-purple-50 dark:bg-purple-950/40',
    pillBorder: 'border-purple-200 dark:border-purple-700',
  },
  pink: {
    bg: 'bg-pink-50 dark:bg-pink-950/40',
    border: 'border-pink-200 dark:border-pink-700',
    header: 'bg-pink-100 dark:bg-pink-900/50',
    headerText: 'text-pink-900 dark:text-pink-100',
    iconColor: 'text-pink-600 dark:text-pink-400',
    pillBg: 'bg-pink-50 dark:bg-pink-950/40',
    pillBorder: 'border-pink-200 dark:border-pink-700',
  },
};

/**
 * Order of colors for cycling when creating new stickies
 */
export const stickyColorOrder: StickyColor[] = ['green', 'purple', 'pink', 'amber', 'sky'];

/**
 * PostgreSQL namespace for user stickies settings
 */
export const USER_STICKIES_NAMESPACE = 'user_stickies';

/**
 * PostgreSQL keys for user stickies
 */
export const USER_STICKIES_KEYS = {
  STICKIES: 'stickies',
  DOCK_COLLAPSED: 'dock_collapsed',
} as const;

/**
 * Default position for new stickies (offset from top-left)
 */
export const DEFAULT_STICKY_POSITION: StickyPosition = {
  x: 100,
  y: 100,
};

/**
 * Default size for new stickies
 */
export const DEFAULT_STICKY_SIZE: StickySize = {
  width: 280,
  height: 200,
};

/**
 * Minimum size constraints for resizable stickies
 */
export const MIN_STICKY_SIZE: StickySize = {
  width: 200,
  height: 150,
};

/**
 * Maximum size constraints for resizable stickies
 * Height is set very high to allow unrestricted vertical resizing
 */
export const MAX_STICKY_SIZE: StickySize = {
  width: 600,
  height: 99999,
};

/**
 * Offset for staggering multiple stickies
 */
export const STICKY_POSITION_OFFSET = 30;

/**
 * Default title for new stickies
 */
export const DEFAULT_STICKY_TITLE = 'New Note';

/**
 * Default placeholder for empty stickies
 */
export const DEFAULT_STICKY_PLACEHOLDER = 'Write your note here...';
